// src/App.jsx
import React from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { AuthProvider, ThemeProvider, useAuth } from './context/AppContext'
import Layout           from './components/Layout'
import LoginPage        from './pages/LoginPage'
import ForcePasswordChangePage from './pages/ForcePasswordChangePage'
import HomePage         from './pages/HomePage'
import CollectionPage   from './pages/CollectionPage'
import BuyersPage      from './pages/BuyersPage'
import UsersPage        from './pages/UsersPage'
import PartnersPage     from './pages/PartnersPage'
import PartnerLeadsPage from './pages/PartnerLeadsPage'
import LeadTimelinePage from './pages/LeadTimelinePage'
import LeadInspectorPage from './pages/LeadInspectorPage'
import EmailReengagementPage from './pages/EmailReengagementPage'
import ConfiguracionPage from './pages/ConfiguracionPage'
import WaterfallValidator from './pages/WaterfallValidator'
import AuctionValidator from './pages/AuctionValidator'
import PartnerDashboard from './pages/Partnerdashboard.jsx'
import StaffLeadsDashboard from './pages/StaffLeadsDashboard.jsx'
import BlockedAtIntakePage from './pages/BlockedAtIntakePage.jsx'
import ReceivablesPage from './pages/ReceivablesPage.jsx'
import PayablesPage from './pages/PayablesPage.jsx'
import CompanyFinancesPage from './pages/CompanyFinancesPage.jsx'
import PWAUpdater       from './components/PWAUpdater'
import InstallPrompt    from './components/InstallPrompt'

import BuyerSetsPage   from './pages/BuyerSetsPage'
import FlowTemplatesPage from './pages/FlowTemplatesPage'
import CompaniesPage  from './pages/CompaniesPage'
import CampaignsPage  from './pages/CampaignsPage'
import CampaignFlowEditor from './pages/CampaignFlowEditor'
import AccountPage from './pages/AccountPage'
import { getSafeNextPath } from './utils/safeRedirect'

function RequireAuth({ children }) {
  const { user, mustChangePassword } = useAuth()
  const location = useLocation()
  if (!user) return <Navigate to="/login" replace />
  if (mustChangePassword && location.pathname !== '/force-password-change') {
    return <Navigate to="/force-password-change" replace />
  }
  return children
}

// Staff-only pages (e.g. internal MCP connector docs) — non-staff users
// (partners, regular users) get bounced back to their normal home page.
// This is UX only, not a security boundary: `user.is_staff` comes from the
// same client-controlled localStorage blob every other role check in this
// app reads, so it's exactly as trustworthy (and exactly as bypassable) as
// the staffOnly/adminOnly nav-hiding this codebase already relies on
// elsewhere. Anything actually sensitive gated behind this route must be
// enforced server-side too.
function RequireStaff({ children }) {
  const { user } = useAuth()
  if (!user?.is_staff) return <Navigate to="/" replace />
  return children
}

// Admin-only pages (the two financial ledgers). Same pattern and same caveats
// as RequireStaff above, one notch up — and specifically so the guard and the
// sidebar link read the *same* predicate: `isAdmin` from AppContext is exactly
// what Layout's `adminOnly` nav flag checks, so a link can't be visible to
// someone the route then bounces, or vice versa.
//
// Note what `isAdmin` actually means here: `is_superuser || is_staff`
// (context/AppContext.jsx). It is NOT superusers-only — every staff user
// passes it. Don't "tighten" it to `is_superuser` on the assumption that
// "admin" must mean something narrower: that would revoke access these pages
// have always granted to staff, which is a permissions change, not a cleanup.
//
// Still UX and not a security boundary, for the reason spelled out on
// RequireStaff: the flags come from the client-controlled localStorage blob.
// The backend enforces this independently — financial/api.py gates every
// endpoint behind django_auth plus permissions_required on
// financial.view_payment / add_payment / view_collection / add_collection.
function RequireAdmin({ children }) {
  const { isAdmin } = useAuth()
  if (!isAdmin) return <Navigate to="/" replace />
  return children
}

// Blocks direct navigation to the force-change screen once it's no longer needed.
function ForcePasswordChangeRoute() {
  const { mustChangePassword } = useAuth()
  return mustChangePassword ? <ForcePasswordChangePage /> : <Navigate to="/" replace />
}

// If the SPA's cached `user` (from localStorage) is stale — e.g. the
// backend session expired or was revoked server-side — a straight
// `Navigate to="/"` here would drop the ?next= param before LoginPage ever
// mounts to read it, stranding anyone bounced here mid-OAuth-flow (like the
// MCP connector setup) on the dashboard instead of finishing their grant.
// Forward to `next` instead when it's present and safe; the backend will
// re-challenge for auth there if the session really is dead.
//
// `next` always points at a Django-only path (e.g. /o/authorize/...) that
// this router doesn't know about, never an SPA route — so it must be a real
// browser navigation. React Router's <Navigate> only pushes client-side
// history; the SPA has no route matching /o/authorize/, so it fell through
// to the catch-all `path="*"` and bounced straight to "/", silently
// dropping the OAuth flow. window.location.replace leaves the SPA and hits
// Django directly, the same way LoginPage's own post-login redirect does.
function LoginRoute() {
  const { user } = useAuth()
  const next = getSafeNextPath(new URLSearchParams(window.location.search).get('next'))
  React.useEffect(() => {
    if (user && next) window.location.replace(next)
  }, [user, next])
  if (!user) return <LoginPage />
  if (next) return null
  return <Navigate to="/" replace />
}

function AppRoutes() {
  const { user } = useAuth()
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />

      <Route path="/force-password-change" element={<RequireAuth><ForcePasswordChangeRoute /></RequireAuth>} />

      <Route path="/" element={<RequireAuth><Layout /></RequireAuth>}>
        <Route index element={<HomeRedirect />} />

        {/* ── Leads ─────────────────────────────────────────────── */}
        <Route path="partner-leads"                     element={<PartnerLeadsPage />} />
        <Route path="admin-leads"                       element={<StaffLeadsDashboard />} />

        <Route path="rejected-submissions"              element={<BlockedAtIntakePage />} />
        <Route path="lead-inspector"                    element={<LeadInspectorPage />} />
        <Route path="lead-inspector/:leadId"           element={<LeadInspectorPage />} />
        <Route path="leads/timeline/:kickoffLeadId"     element={<LeadTimelinePage />} />
        <Route path="lead-logs"
          element={<CollectionPage col="lead-logs"        title="Lead Logs"        />} />

        {/* ── Email Reengagement (capa de remarketing email) ────────── */}
        <Route path="email-reengagement" element={<EmailReengagementPage />} />
        <Route path="hyalite" element={<Navigate to="/email-reengagement" replace />} />

        {/* ── Validators & Analytics ─────────────────────────────── */}
        <Route path="waterfall-validator" element={<WaterfallValidator />} />
        <Route path="auction-validator"   element={<AuctionValidator />} />

        {/* ── Partner Dashboard (exclusivo para usuarios partner) ── */}
        <Route path="partner-dashboard" element={<PartnerDashboard />} />
        <Route path="partner-dashboard/campaigns/:campaignId" element={<PartnerDashboard />} />
        <Route path="partner-dashboard/buyer-campaigns/:buyerCampaignId" element={<PartnerDashboard />} />

        {/* ── Configuración ─────────────────────────────────────── */}
        <Route path="configuracion"   element={<ConfiguracionPage />} />
        <Route path="companies"       element={<CompaniesPage />} />
        <Route path="affiliates"        element={<PartnersPage />} />
        {/* Financial se partió en dos ledgers. Los nombres no comparten
            prefijo a propósito: el menú casa la ruta activa con
            `startsWith`, y un /financial-* colisionaría con el /financial de
            abajo. */}
        <Route path="receivables"     element={<RequireAdmin><ReceivablesPage /></RequireAdmin>} />
        <Route path="payables"        element={<RequireAdmin><PayablesPage /></RequireAdmin>} />
        {/* Marcadores y enlaces viejos a la página única, igual que hyalite
            arriba. A receivables porque es la primera de la sección. */}
        <Route path="financial"       element={<Navigate to="/receivables" replace />} />
        {/* Los libros propios de la empresa (backend `corporate/`), aparte de
            la economía de los leads. Sin RequireAdmin a propósito: el acceso
            lo decide el servidor contra el grupo `Finance`, que no tiene
            ninguna relación con `isAdmin` — envolverla echaría a un miembro de
            Finance que no sea admin a "/" sin explicación, cuando el backend
            sí le daría los datos. Quien no tenga acceso ve la pantalla de
            acceso denegado de la propia página, que es la respuesta honesta. */}
        <Route path="company-finances" element={<CompanyFinancesPage />} />
        <Route path="buyers"         element={<BuyersPage />} />
        <Route path="buyer-sets"     element={<BuyerSetsPage />} />
        <Route path="flow-templates" element={<FlowTemplatesPage />} />
        <Route path="tokens"
          element={<CollectionPage col="tokens"           title="Tokens"           />} />
        <Route path="tcpa"
          element={<CollectionPage col="tcpa"             title="TCPA"             />} />
        <Route path="campaigns"       element={<CampaignsPage />} />

        {/* ── Logs & Control ────────────────────────────────────── */}
        <Route path="sms-suppression"
          element={<CollectionPage col="sms-suppression"  title="SMS Suppression"  />} />

        {/* ── Admin ─────────────────────────────────────────────── */}
        <Route path="users" element={<UsersPage />} />
        <Route path="account" element={<RequireStaff><AccountPage /></RequireStaff>} />
      </Route>

      {/* ── Fullscreen Standalone Flow Editor ───────────────────────── */}
      <Route path="/campaigns/:id/flow" element={<RequireAuth><CampaignFlowEditor /></RequireAuth>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

// Pequeño componente que decide qué mostrar en la ruta raíz según el rol.
// Si es partner, lo mandamos a su dashboard. Si no, ve el Home (Business overview).
function HomeRedirect() {
  const { isPartner } = useAuth()
  if (isPartner) return <Navigate to="/partner-dashboard" replace />
  return <HomePage />
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter basename="/">
          <AppRoutes />
          <PWAUpdater />
          <InstallPrompt />
          <Toaster
            position="top-right"
            toastOptions={{
              style: { fontFamily: 'DM Sans, sans-serif', fontSize: '13px' },
              duration: 3500,
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  )
}
